/**
 * Anzeige kontrollierter Fachbegriffe.
 *
 * Sprachebene 2 nach `PRODUCT_DATA_MODEL.md` §15: **eine Wahrheit, mehrere
 * Anzeigen.** Der gespeicherte Fachwert bleibt unveraendert; uebersetzt wird
 * ausschliesslich, was der Benutzer liest.
 *
 * Es entsteht hier KEIN zweiter Terminologiebestand. Beide Funktionen benutzen
 * `tCode` aus dem bestehenden Woerterbuch -- sie geben dem Aufruf nur einen
 * Namen, damit an den Anzeigestellen erkennbar bleibt, WAS uebersetzt wird.
 *
 * Die Rueckfallregel ist dieselbe wie ueberall: ist ein Wert nicht im
 * Woerterbuch, wird der **gespeicherte Originalwert** angezeigt -- niemals
 * leer, niemals der technische Schluessel, niemals eine erfundene
 * Uebersetzung. Der Rueckfall ist ein Sicherheitsnetz, kein bestandener Audit:
 * `checki18n` und `verify` melden fehlende Zuordnungen.
 */
import type { I18n } from "./index.tsx";
import type { TranslationKey } from "./de.ts";

/**
 * Anzeigename einer Produktkategorie.
 *
 * Acht der zehn Kategorien sind sprachneutral und besitzen keinen Eintrag --
 * sie erscheinen in beiden Sprachen unveraendert.
 */
export const categoryLabel = (i18n: I18n, code: string, storedName: string): string =>
  i18n.tCode("category", code, storedName);

/**
 * Anzeigename einer Produktfamilie.
 *
 * 34 der 36 Familien sind Eigennamen und bleiben unveraendert. Uebersetzt
 * werden nur `Treiber` und `LED-Notlichtbeleuchtung` -- beide beschreiben
 * einen Gegenstand, statt ihn zu benennen.
 */
/**
 * EIN PRODUKTMODELL EINMAL NENNEN -- auch wenn es zwei Felder hat (§9).
 *
 * DER BEFUND DER BROWSER-ABNAHME. Im Reiter „Modell" stand
 * „HB200-U | HB200-U": Modellcode und Modellname tragen bei diesem
 * Modell denselben Wert, und die Oberflaeche setzte beide nebeneinander.
 * Zwei gleiche Links nebeneinander sehen aus wie ein Fehler, und sie
 * sind einer.
 *
 * SIE SIND TROTZDEM ZWEI FELDER, und das bleibt richtig: der Code ist
 * der technische Schluessel, der Name die Benennung. Bei TP154 heissen
 * sie „TP154" und „TP PRO TP154" und sagen Verschiedenes. Nur wenn
 * beide dasselbe sagen, ist es EINE Angabe.
 *
 * Verglichen wird nach Leerraum und Gross-/Kleinschreibung: „HB200-U"
 * und „hb200-u " sind nicht zwei Angaben.
 */
export function modelLabel(code: string, name: string): { code: string; name: string | null } {
  const schlank = (x: string): string => x.trim().toLowerCase();
  return { code: code.trim(), name: schlank(code) === schlank(name) ? null : name.trim() };
}

export const familyLabel = (i18n: I18n, code: string, storedName: string): string =>
  i18n.tCode("family", code, storedName);

/** Anzeige der Herkunft einer Angabe. Heute noch an keiner Stelle sichtbar. */
export const provenanceLabel = (i18n: I18n, code: string): string =>
  i18n.tCode("provenance", code, code);

/**
 * Der Anzeigename eines MERKMALS (Produktmodelle 003).
 *
 * Reihenfolge: ausdruecklich vergebener Name, sonst die gepflegte
 * Uebersetzung `prop.<code>`, sonst der Code. Die 39 gewachsenen Merkmale
 * tragen keinen eigenen Namen -- fuer sie bleibt alles, wie es war; ein im
 * Betrieb angelegtes Merkmal haette dagegen nie eine Uebersetzung und
 * hiesse sonst fuer immer wie sein Code.
 */
export const propertyLabel = (
  i18n: I18n,
  def: { code: string; nameDe?: string | null; nameEn?: string | null },
  kontext?: Merkmalskontext,
): string => {
  const abweichend = kontextname(def.code, kontext);
  if (abweichend !== null) return i18n.t(abweichend);
  const eigen = i18n.lang === "en" ? (def.nameEn ?? def.nameDe) : (def.nameDe ?? def.nameEn);
  return eigen !== null && eigen !== undefined && eigen !== ""
    ? eigen : i18n.tCode("prop", def.code, def.code);
};

/**
 * Woran dieses Merkmal gerade steht. Heute genau eine Frage.
 *
 * `controlGearSupply` ist der gepflegte Lieferzustand des Artikels
 * (`integriert` | `separat` | `ohne`) oder null, solange niemand ihn
 * beantwortet hat.
 */
export interface Merkmalskontext {
  readonly controlGearSupply?: string | null;
}

/**
 * DIESELBE GROESSE, ZWEI SEITEN DERSELBEN SCHNITTSTELLE (Restpunkte 002).
 *
 * `dc_output_voltage` und `dc_output_current` heissen im Katalog nach ihrer
 * Treiberseite -- dort sind sie ein AUSGANG, und das ist richtig so. An einer
 * Leuchte, die ohne oder mit separatem Betriebsgeraet geliefert wird, stehen
 * dieselben Zahlen fuer das Gegenteil: den Bereich, in dem sie betrieben
 * werden DARF. „DC-Ausgangsspannung" an einem Panel behauptet einen Ausgang,
 * den es nicht hat.
 *
 * KEIN ZWEITES MERKMAL. Zwei Katalogeintraege fuer dieselbe physikalische
 * Groesse waeren zwei Wahrheiten, die beim ersten Abgleich auseinanderlaufen
 * -- und die Umbenennung ist keine fachliche, sondern eine sprachliche
 * Frage. Sie gehoert deshalb hierher: diese Datei ist die Stelle, an der aus
 * EINER Wahrheit MEHRERE Anzeigen werden (Sprachebene 2).
 *
 * Nur bei „ohne" und „separat". Bei integriertem Geraet gehoert die
 * DC-Seite der Leuchte gar nicht, und ohne gepflegten Lieferzustand wird
 * nichts unterstellt -- dann bleibt der Katalogname stehen.
 */
const DC_AN_DER_LEUCHTE: Readonly<Record<string, TranslationKey>> = {
  dc_output_voltage: "prop.dc_output_voltage.atLuminaire",
  dc_output_current: "prop.dc_output_current.atLuminaire",
};

const kontextname = (code: string, k?: Merkmalskontext): TranslationKey | null => {
  const z = k?.controlGearSupply;
  if (z !== "ohne" && z !== "separat") return null;
  return DC_AN_DER_LEUCHTE[code] ?? null;
};
